import { api } from '@/packages/api/src';
import type { DetailedInvoice, InvoiceIndexEntry } from '@/packages/api/src';
import { getCurrentOrganizationId } from '@/utils/useUser';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, toValue, type MaybeRefOrGetter } from 'vue';

type QueryOptions = {
    enabled?: MaybeRefOrGetter<boolean>;
    status?: MaybeRefOrGetter<InvoiceStatus | undefined>;
};

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'cancelled';

function isEnabled(enabled: QueryOptions['enabled']) {
    return enabled === undefined ? true : toValue(enabled);
}

export function invoicesQueryKey(
    organizationId: string | null = getCurrentOrganizationId(),
    status?: InvoiceStatus
) {
    return ['invoices', organizationId, status] as const;
}

export function invoiceQueryKey(
    invoiceId: string,
    organizationId: string | null = getCurrentOrganizationId()
) {
    return ['invoice', organizationId, invoiceId] as const;
}

export function invoiceSettingsQueryKey(organizationId: string | null = getCurrentOrganizationId()) {
    return ['invoiceSettings', organizationId] as const;
}

export async function fetchInvoices(
    organizationId: string,
    status?: InvoiceStatus
): Promise<InvoiceIndexEntry[]> {
    const response = await api.getInvoices({
        params: {
            organization: organizationId,
        },
        queries: { status },
    });
    return response.data;
}

export async function fetchInvoice(organizationId: string, invoiceId: string): Promise<DetailedInvoice> {
    const response = await api.getInvoice({
        params: {
            organization: organizationId,
            invoice: invoiceId,
        },
    });
    return response.data;
}

export async function fetchInvoiceSettings(organizationId: string) {
    const response = await api.getInvoiceSettings({
        params: {
            organization: organizationId,
        },
    });
    return response.data;
}

export function useInvoicesQuery(options: QueryOptions = {}) {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: computed(() => invoicesQueryKey(getCurrentOrganizationId(), toValue(options.status))),
        queryFn: async () => {
            const organizationId = getCurrentOrganizationId();
            if (organizationId === null) {
                throw new Error('No current organization id - fetch invoices');
            }
            return fetchInvoices(organizationId, toValue(options.status));
        },
        enabled: computed(() => isEnabled(options.enabled) && getCurrentOrganizationId() !== null),
        staleTime: 1000 * 30,
    });

    const invoices = computed(() => query.data.value ?? []);

    const invalidateInvoices = () => {
        queryClient.invalidateQueries({ queryKey: ['invoices'] });
    };

    return {
        ...query,
        invoices,
        invalidateInvoices,
    };
}

export function useInvoiceQuery(invoiceId: MaybeRefOrGetter<string>, options: QueryOptions = {}) {
    const query = useQuery({
        queryKey: computed(() => invoiceQueryKey(toValue(invoiceId))),
        queryFn: async () => {
            const organizationId = getCurrentOrganizationId();
            if (organizationId === null) {
                throw new Error('No current organization id - fetch invoice');
            }
            return fetchInvoice(organizationId, toValue(invoiceId));
        },
        enabled: computed(
            () =>
                isEnabled(options.enabled) &&
                getCurrentOrganizationId() !== null &&
                toValue(invoiceId) !== ''
        ),
        staleTime: 1000 * 30,
    });

    return {
        ...query,
    };
}

export function useInvoiceSettingsQuery(options: QueryOptions = {}) {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: computed(() => invoiceSettingsQueryKey()),
        queryFn: async () => {
            const organizationId = getCurrentOrganizationId();
            if (organizationId === null) {
                throw new Error('No current organization id - fetch invoice settings');
            }
            return fetchInvoiceSettings(organizationId);
        },
        enabled: computed(() => isEnabled(options.enabled) && getCurrentOrganizationId() !== null),
        staleTime: 1000 * 30,
    });

    const invalidateInvoiceSettings = () => {
        queryClient.invalidateQueries({ queryKey: ['invoiceSettings'] });
    };

    return {
        ...query,
        invalidateInvoiceSettings,
    };
}
